import React, { useState, useEffect } from 'react';
import QRCode from "react-qr-code";
import { X, Download, Printer, ExternalLink, ShieldCheck, Users, Key, Link as LinkIcon, History, AlertCircle, Eye, EyeOff, Plus, Edit, Power, Trash2, Mail, Monitor, Building2 } from 'lucide-react';
import { Hospital, StaffUser, StaffRole, STAFF_ROLES, AuditEntry } from '../types';
import * as storageService from '../services/storageService';

interface AccessModalProps {
    isOpen: boolean;
    onClose: () => void;
    hospital: Hospital | null;
    onHospitalUpdate?: () => void; // Para recargar el dashboard si cambia el PIN maestro
}

const AccessModal: React.FC<AccessModalProps> = ({ isOpen, onClose, hospital, onHospitalUpdate }) => {
    const [activeTab, setActiveTab] = useState<'info' | 'security' | 'staff' | 'audit'>('info');

    // -- PIN MAESTRO STATE --
    const [masterPin, setMasterPin] = useState('');
    const [showMasterPin, setShowMasterPin] = useState(false);
    const [isSavingPin, setIsSavingPin] = useState(false);
    const [revealedPin, setRevealedPin] = useState<string | null>(null);
    const [isRevealingPin, setIsRevealingPin] = useState(false);
    const [showRevealedPin, setShowRevealedPin] = useState(false);

    // -- STAFF STATE --
    const [staffList, setStaffList] = useState<StaffUser[]>([]);
    const [isLoadingStaff, setIsLoadingStaff] = useState(false);
    const [editingStaff, setEditingStaff] = useState<StaffUser | null>(null);
    const [isStaffFormOpen, setIsStaffFormOpen] = useState(false);
    const [staffForm, setStaffForm] = useState({ name: '', role: 'OPERATIVO' as StaffRole, pin: '', pinConfirm: '', email: '' });
    const [staffFormError, setStaffFormError] = useState<string | null>(null);
    const [isSavingStaff, setIsSavingStaff] = useState(false);
    const [showStaffFormPin, setShowStaffFormPin] = useState(false);

    // -- AUDIT LOG STATE --
    const [auditLogs, setAuditLogs] = useState<AuditEntry[]>([]);
    const [isLoadingAudit, setIsLoadingAudit] = useState(false);

    useEffect(() => {
        if (isOpen && hospital) {
            setActiveTab('info');
            setMasterPin(''); // Vacío por seguridad, solo se usa si se edita
            setRevealedPin(null);
            setShowRevealedPin(false);
            loadStaffData(hospital.id);
            loadAuditData(hospital.id);
        }
    }, [isOpen, hospital]);

    const loadStaffData = async (hId: string) => {
        setIsLoadingStaff(true);
        try {
            const list = await storageService.getStaffByHospital(hId);
            setStaffList(list);
        } catch (e) {
            console.error("Error cargando staff:", e);
        } finally {
            setIsLoadingStaff(false);
        }
    };

    const loadAuditData = async (hId: string) => {
        setIsLoadingAudit(true);
        try {
            const logs = await storageService.getAuditLog(hId);
            setAuditLogs(logs);
        } catch (e) {
            console.error("Error cargando audit logs:", e);
        } finally {
            setIsLoadingAudit(false);
        }
    };

    if (!isOpen || !hospital) return null;

    const baseUrl = window.location.origin;
    const publicLink = `${baseUrl}/${hospital.handle}`;
    const staffLink = `${baseUrl}/${hospital.handle}/staff`;

    // --- HANDLERS ---
    const handleSaveMasterPin = async () => {
        if (masterPin.trim() && !/^\d{4}$/.test(masterPin)) {
            alert('El PIN Maestro debe ser de exactamente 4 dígitos.');
            return;
        }

        setIsSavingPin(true);
        try {
            const updated = { ...hospital, staffPin: masterPin.trim() || hospital.staffPin };
            await storageService.saveHospital(updated);
            alert('PIN Maestro actualizado correctamente.');
            // Update revealed PIN so the display stays current
            if (masterPin.trim()) {
                setRevealedPin(masterPin.trim());
                setShowRevealedPin(true);
            }
            setMasterPin('');
            setShowMasterPin(false);
            if (onHospitalUpdate) onHospitalUpdate();
        } catch (e) {
            console.error(e);
            alert('Error al guardar el PIN Maestro.');
        } finally {
            setIsSavingPin(false);
        }
    };

    const handleStaffSave = async () => {
        setStaffFormError(null);
        if (!staffForm.name.trim()) { setStaffFormError('El nombre es requerido.'); return; }

        const isAdminRole = ['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(staffForm.role);

        if (isAdminRole) {
            if (!staffForm.email) {
                setStaffFormError('Debes ingresar un correo electrónico para administradores.');
                return;
            }
            if (!staffForm.email.includes('@')) {
                setStaffFormError('Ingresa un correo válido.');
                return;
            }
        } else {
            // Reglas para Roles Operativos con PIN
            if (!editingStaff && !staffForm.pin) { setStaffFormError('El PIN es requerido.'); return; }
            if (staffForm.pin && !/^\d{4}$/.test(staffForm.pin)) { setStaffFormError('El PIN debe ser de 4 dígitos.'); return; }
            if (staffForm.pin && staffForm.pin !== staffForm.pinConfirm) { setStaffFormError('Los PINs no coinciden.'); return; }
        }

        setIsSavingStaff(true);
        try {
            if (editingStaff) {
                const updates: any = { name: staffForm.name, role: staffForm.role };
                
                if (isAdminRole) {
                    updates.email = staffForm.email;
                    updates.pin = null; // Quitar pin si lo tenía
                } else {
                    if (staffForm.pin) updates.pin = staffForm.pin;
                    updates.email = null;
                }
                
                const ok = await storageService.updateStaffUser(editingStaff.id, updates);
                if (!ok) {
                    setStaffFormError('Error al actualizar el usuario. Verifica permisos.');
                    return;
                }
            } else {
                const newData: any = { 
                    name: staffForm.name, 
                    role: staffForm.role, 
                    pin: isAdminRole ? undefined : staffForm.pin,
                    email: isAdminRole ? staffForm.email : undefined
                };
                const result = await storageService.createStaffUser(hospital.id, newData);
                if (!result.success) {
                    setStaffFormError(result.error || 'Error desconocido al crear el usuario.');
                    return;
                }
            }
            setIsStaffFormOpen(false);
            loadStaffData(hospital.id);
            setStaffForm({ name: '', role: 'OPERATIVO', pin: '', pinConfirm: '', email: '' });
        } catch (e: any) {
            setStaffFormError(e?.message || 'Ocurrió un error al guardar el usuario.');
        } finally {
            setIsSavingStaff(false);
        }
    };

    const ROLE_COLORS: Record<StaffRole, string> = {
        SUPER_ADMIN: 'bg-red-500/10 text-red-600 border-red-500/20',
        GROUP_ADMIN: 'bg-indigo-500/10 text-indigo-600 border-indigo-500/20',
        HOSPITAL_ADMIN: 'bg-purple-500/10 text-purple-600 border-purple-500/20',
        FINANCIERO: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
        MARKETING: 'bg-pink-500/10 text-pink-600 border-pink-500/20',
        OPERATIVO: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    };
    const ROLE_LABELS: Record<StaffRole, string> = {
        SUPER_ADMIN: 'Super Admin',
        GROUP_ADMIN: 'Dir. Grupo',
        HOSPITAL_ADMIN: 'Admin',
        FINANCIERO: 'Finanzas',
        MARKETING: 'Marketing',
        OPERATIVO: 'Operativo',
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden flex flex-col my-auto max-h-[90vh]">

                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                            <ShieldCheck className="w-6 h-6 text-indigo-600" />
                            Centro de Accesos
                        </h2>
                        <p className="text-sm text-slate-500 font-medium mt-1">{hospital.name}</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full transition-colors">
                        <X className="w-5 h-5 text-slate-400" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-slate-100 px-6 gap-6 overflow-x-auto bg-white">
                    {[
                        { id: 'info', label: 'Enlaces y QR', icon: LinkIcon },
                        { id: 'security', label: 'PIN Maestro', icon: Key },
                        { id: 'staff', label: 'Personal y Accesos', icon: Users },
                        { id: 'audit', label: 'Registro Log', icon: History },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id as any)}
                            className={`flex items-center gap-2 py-4 border-b-2 text-sm font-bold transition-colors whitespace-nowrap ${activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400 hover:text-slate-600'
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">

                    {/* TAB: INFO Y QR - SEPARACIÓN RBAC */}
                    {activeTab === 'info' && (
                        <div className="flex flex-col gap-6 animate-fade-in pb-4">
                            {/* 1. Recepción Pública (Familias) */}
                            <div className="bg-white rounded-3xl p-6 flex flex-col md:flex-row gap-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                                <div className="flex-1 flex flex-col justify-center">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="bg-slate-100 p-2 rounded-xl text-slate-500">
                                            <Monitor className="w-5 h-5" />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-800">
                                            Recepción Digital <span className="text-slate-400 font-medium">(Familias)</span>
                                        </h3>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-6 leading-relaxed">Vista pública anónima estilo aeropuerto para las pantallas en salas de espera.</p>
                                    
                                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Enlace Directo</label>
                                    <div className="flex items-center gap-2 bg-slate-50/50 p-2.5 rounded-2xl border border-slate-100 transition-colors hover:bg-slate-50">
                                        <code className="flex-1 text-sm text-slate-600 px-2 truncate select-all">{publicLink}</code>
                                        <a href={publicLink} target="_blank" rel="noreferrer" className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-xl shadow-sm border border-transparent hover:border-slate-200 transition-all">
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-center items-center w-full md:w-auto md:min-w-[120px]">
                                    <div className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 mb-3 hover:scale-105 transition-transform">
                                        <QRCode value={publicLink} size={90} level={'L'} fgColor="#475569" />
                                    </div>
                                    <button className="text-[10px] font-bold text-slate-400 hover:text-indigo-600 uppercase tracking-wider flex items-center gap-1.5 transition-colors">
                                        <Printer className="w-3.5 h-3.5" /> Imprimir QR
                                    </button>
                                </div>
                            </div>

                            {/* 2. Equipo Médico (Quirófanos Zero-Trust) */}
                            <div className="bg-gradient-to-br from-indigo-50/50 to-white rounded-3xl p-6 flex flex-col md:flex-row gap-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-indigo-100/50 transition-all hover:shadow-[0_8px_30px_rgb(79,70,229,0.08)]">
                                <div className="flex-1 flex flex-col justify-center">
                                    <div className="flex flex-wrap items-center gap-3 mb-2">
                                        <div className="bg-indigo-100 text-indigo-600 p-2 rounded-xl shadow-sm border border-indigo-200/50">
                                            <ShieldCheck className="w-5 h-5" />
                                        </div>
                                        <h3 className="text-xl font-bold text-slate-800">
                                            Equipo Operativo <span className="text-indigo-600/70 font-medium">(Quirófano)</span>
                                        </h3>
                                        <span className="bg-indigo-600 text-white text-[9px] px-2 py-0.5 rounded-md font-bold tracking-widest shadow-sm shadow-indigo-500/30 uppercase flex items-center gap-1">
                                            Zero Trust
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                                        Escanean el código QR con su celular e ingresan con su PIN personal de 4 dígitos.
                                    </p>
                                    
                                    <label className="block text-[10px] font-bold text-indigo-400/80 uppercase tracking-widest mb-2">Enlace de Quirofáno</label>
                                    <div className="flex items-center gap-2 bg-white/60 p-2.5 rounded-2xl border border-indigo-100 shadow-sm transition-colors hover:bg-white">
                                        <code className="flex-1 text-sm text-indigo-700 font-bold px-2 truncate select-all">{staffLink}</code>
                                        <a href={staffLink} target="_blank" rel="noreferrer" className="p-2 text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 rounded-xl shadow-sm border border-transparent hover:border-indigo-100 transition-all">
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-center items-center w-full md:w-auto md:min-w-[120px]">
                                    <div className="bg-white p-3 rounded-2xl shadow-sm border border-indigo-100 mb-3 hover:scale-105 transition-transform relative">
                                        <div className="absolute inset-0 bg-indigo-500/5 blur-xl rounded-full"></div>
                                        <QRCode value={staffLink} size={90} level={'M'} fgColor="#4f46e5" className="relative z-10" />
                                    </div>
                                    <button className="text-[10px] font-bold text-indigo-500 hover:text-indigo-700 uppercase tracking-wider flex items-center gap-1.5 transition-colors">
                                        <Printer className="w-3.5 h-3.5" /> Imprimir QR
                                    </button>
                                </div>
                            </div>

                            {/* 3. Administradores (Cloudflare / Corporativos) */}
                            <div className="bg-gradient-to-br from-emerald-50/40 to-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-emerald-100/50 transition-all hover:shadow-[0_8px_30px_rgb(16,185,129,0.08)]">
                                <div className="flex flex-wrap items-center gap-3 mb-2">
                                    <div className="bg-emerald-100 text-emerald-600 p-2 rounded-xl shadow-sm border border-emerald-200/50">
                                        <Building2 className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">
                                        Directores y Admins <span className="text-emerald-600/70 font-medium">(Corporativo)</span>
                                    </h3>
                                    <span className="bg-emerald-600 text-white text-[9px] px-2 py-0.5 rounded-md font-bold tracking-widest shadow-sm shadow-emerald-500/30 uppercase">
                                        Cloudflare Protected
                                    </span>
                                </div>
                                <p className="text-sm text-slate-500 mb-6 leading-relaxed">
                                    Acceden al "Workspace" para administrar analytics, personal y televisores con seguridad de red.
                                </p>
                                
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="flex-1 bg-white/60 p-5 rounded-3xl border border-emerald-100 shadow-sm transition-all hover:shadow-md hover:bg-white flex flex-col justify-center">
                                        <label className="block text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                            <span className="bg-emerald-100 text-emerald-600 w-4 h-4 rounded-full flex items-center justify-center text-[10px]">1</span> Entrar al Portal
                                        </label>
                                        <div className="flex items-center gap-2">
                                            <code className="text-sm text-slate-800 font-bold truncate flex-1 px-1">{baseUrl}/login</code>
                                            <a href={`${baseUrl}/login`} target="_blank" rel="noreferrer" className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-xl transition-colors border border-transparent hover:border-emerald-100">
                                                <ExternalLink className="w-4 h-4" />
                                            </a>
                                        </div>
                                    </div>
                                    <div className="flex-1 bg-white/60 p-5 rounded-3xl border border-emerald-100 shadow-sm transition-all hover:shadow-md hover:bg-white flex flex-col justify-center relative overflow-hidden group">
                                        <div className="absolute -right-4 -top-4 w-24 h-24 bg-emerald-500/5 rounded-full blur-2xl group-hover:bg-emerald-500/10 transition-colors"></div>
                                        <label className="block text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-2 relative z-10 flex items-center gap-1.5">
                                            <span className="bg-emerald-100 text-emerald-600 w-4 h-4 rounded-full flex items-center justify-center text-[10px]">2</span> ID del Workspace
                                        </label>
                                        <code className="text-xl font-mono text-emerald-700 font-bold truncate select-all relative z-10 px-1">{hospital.handle || hospital.id}</code>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB: SEGURIDAD PIN MAESTRO */}
                    {activeTab === 'security' && (
                        <div className="animate-fade-in max-w-2xl mx-auto py-4">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Key className="w-8 h-8 text-emerald-600" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800">PIN Maestro del Hospital</h3>
                                <p className="text-slate-500 mt-2 text-sm max-w-md mx-auto">
                                    El PIN maestro es la contraseña compartida "legacy" para entrar al panel si los usuarios individuales no están configurados.
                                </p>
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm mb-6">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">PIN Actual</label>
                                {revealedPin !== null ? (
                                    <div className="flex items-center justify-center gap-4 py-4">
                                        <div className="text-4xl font-mono font-black tracking-[0.5em] text-slate-800 select-all">
                                            {showRevealedPin ? revealedPin : '••••'}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowRevealedPin(!showRevealedPin)}
                                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                        >
                                            {showRevealedPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={async () => {
                                            setIsRevealingPin(true);
                                            const pin = await storageService.revealHospitalPin(hospital.id);
                                            setRevealedPin(pin || '(no disponible)');
                                            setShowRevealedPin(true);
                                            setIsRevealingPin(false);
                                        }}
                                        disabled={isRevealingPin}
                                        className="w-full py-4 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold text-sm transition-all flex justify-center items-center gap-2 disabled:opacity-50"
                                    >
                                        {isRevealingPin ? <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" /> : <Eye className="w-5 h-5" />}
                                        Revelar PIN Actual
                                    </button>
                                )}
                            </div>

                            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm">
                                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Actualizar PIN Maestro</label>
                                <div className="flex flex-col sm:flex-row gap-4 mb-3">
                                    <div className="relative flex-1">
                                        <input
                                            type={showMasterPin ? 'text' : 'password'}
                                            value={masterPin}
                                            onChange={(e) => setMasterPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                                            className="w-full text-center text-3xl font-mono tracking-[0.5em] font-bold p-4 rounded-xl border-2 border-slate-200 text-slate-800 focus:outline-none focus:border-indigo-500 transition-colors"
                                            placeholder="••••"
                                            maxLength={4}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowMasterPin(!showMasterPin)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                        >
                                            {showMasterPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setMasterPin(Math.floor(1000 + Math.random() * 9000).toString());
                                            setShowMasterPin(true);
                                        }}
                                        className="bg-slate-100 text-slate-700 px-6 py-4 rounded-xl font-bold hover:bg-slate-200 transition-colors whitespace-nowrap"
                                    >
                                        Generar Al Azar
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400 mb-6 font-medium text-center">
                                    Deja el campo vacío para no modificar el PIN actual.
                                </p>

                                <button
                                    onClick={handleSaveMasterPin}
                                    disabled={isSavingPin || (masterPin.length > 0 && masterPin.length < 4)}
                                    className="w-full py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex justify-center items-center gap-2"
                                >
                                    {isSavingPin ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <ShieldCheck className="w-5 h-5" />}
                                    Guardar Nuevo PIN Maestro
                                </button>
                            </div>
                        </div>
                    )}

                    {/* TAB: STAFF USERS */}
                    {activeTab === 'staff' && (
                        <div className="animate-fade-in flex flex-col h-full">
                            {!isStaffFormOpen ? (
                                <>
                                    <div className="flex justify-between items-center mb-6">
                                        <div>
                                            <h3 className="font-bold text-slate-800 text-lg">Directorio de Personal</h3>
                                            <p className="text-sm text-slate-500">Administra los accesos individuales mediante doble PIN.</p>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setEditingStaff(null);
                                                setStaffForm({ name: '', role: 'OPERATIVO', pin: '', pinConfirm: '' });
                                                setStaffFormError(null);
                                                setShowStaffFormPin(false);
                                                setIsStaffFormOpen(true);
                                            }}
                                            className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-5 rounded-xl flex items-center gap-2 shadow-sm transition-all text-sm"
                                        >
                                            <Plus className="w-4 h-4" /> Agregar Usuario
                                        </button>
                                    </div>

                                    {isLoadingStaff ? (
                                        <div className="flex-1 flex items-center justify-center p-12">
                                            <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                        </div>
                                    ) : staffList.length === 0 ? (
                                        <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center flex-1 flex flex-col items-center justify-center">
                                            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                            <p className="text-slate-600 font-bold text-lg mb-2">Sin usuarios individuales</p>
                                            <p className="text-slate-500 text-sm max-w-sm">
                                                Crea cuentas individuales para habilitar el login de <strong className="text-indigo-600">doble PIN</strong> y mantener un registro de auditoría exacto.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
                                            <table className="w-full">
                                                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                                                    <tr>
                                                        <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Nombre</th>
                                                        <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Rol</th>
                                                        <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Estado</th>
                                                        <th className="py-4 px-6 text-xs font-bold text-slate-400 uppercase tracking-wider">Alta</th>
                                                        <th className="py-4 px-6"></th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {staffList.map(member => (
                                                        <tr key={member.id} className={`transition-colors hover:bg-slate-50 ${!member.isActive ? 'opacity-50 grayscale' : ''}`}>
                                                            <td className="py-4 px-6">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-600">
                                                                        {member.name.charAt(0).toUpperCase()}
                                                                    </div>
                                                                    <span className="text-sm font-bold text-slate-700">{member.name}</span>
                                                                </div>
                                                            </td>
                                                            <td className="py-4 px-6">
                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${ROLE_COLORS[member.role]}`}>
                                                                    {ROLE_LABELS[member.role]}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-6">
                                                                <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${member.isActive ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                                    <span className={`w-1.5 h-1.5 rounded-full ${member.isActive ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                                    {member.isActive ? 'Activo' : 'Inactivo'}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-6 text-xs text-slate-400 font-medium">
                                                                {new Date(member.createdAt).toLocaleDateString()}
                                                            </td>
                                                            <td className="py-4 px-6">
                                                                <div className="flex items-center gap-1 min-w-[100px] justify-end">
                                                                    <button
                                                                        onClick={() => {
                                                                            setEditingStaff(member);
                                                                            setStaffForm({ name: member.name, role: member.role, pin: '', pinConfirm: '', email: member.email || '' });
                                                                            setStaffFormError(null);
                                                                            setShowStaffFormPin(false);
                                                                            setIsStaffFormOpen(true);
                                                                        }}
                                                                        className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                                                        title="Editar"
                                                                    >
                                                                        <Edit className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={async () => {
                                                                            await storageService.updateStaffUser(member.id, { isActive: !member.isActive });
                                                                            loadStaffData(hospital.id);
                                                                        }}
                                                                        className={`p-2 rounded-lg transition-all ${member.isActive ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                                                                        title={member.isActive ? "Desactivar" : "Activar"}
                                                                    >
                                                                        <Power className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={async () => {
                                                                            if (window.confirm(`¿Seguro que deseas eliminar el acceso de ${member.name}?`)) {
                                                                                await storageService.deleteStaffUser(member.id);
                                                                                loadStaffData(hospital.id);
                                                                            }
                                                                        }}
                                                                        className="p-2 text-red-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                                                        title="Eliminar"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            ) : (
                                // --- STAFF FORM ---
                                <div className="bg-white border border-slate-200 rounded-3xl p-8 shadow-sm max-w-2xl mx-auto w-full">
                                    <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-3">
                                        <button onClick={() => setIsStaffFormOpen(false)} className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors">
                                            <X className="w-4 h-4" />
                                        </button>
                                        {editingStaff ? 'Editar Acceso de Personal' : 'Nuevo Acceso de Personal'}
                                    </h3>

                                    <div className="space-y-5">
                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Nombre Completo</label>
                                            <input
                                                type="text"
                                                value={staffForm.name}
                                                onChange={e => setStaffForm(f => ({ ...f, name: e.target.value }))}
                                                placeholder="Ej: Dr. Roberto Gómez"
                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-700 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                            />
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 block">Nivel de Acceso (Rol)</label>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                {STAFF_ROLES.map(r => (
                                                    <button
                                                        key={r.value}
                                                        type="button"
                                                        onClick={() => setStaffForm(f => ({ ...f, role: r.value }))}
                                                        className={`flex flex-col items-start p-4 rounded-xl border-2 transition-all ${staffForm.role === r.value
                                                            ? 'border-indigo-600 bg-indigo-50 shadow-sm'
                                                            : 'border-slate-100 bg-white hover:border-slate-200'
                                                            }`}
                                                    >
                                                        <div className={`text-xs font-bold mb-1 ${staffForm.role === r.value ? 'text-indigo-800' : 'text-slate-700'}`}>{r.label}</div>
                                                        <div className={`text-[10px] text-left leading-tight ${staffForm.role === r.value ? 'text-indigo-600/80' : 'text-slate-500'}`}>{r.description}</div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <span>
                                                        {['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(staffForm.role)
                                                            ? 'Correo Electrónico (Cloudflare Zero Trust)'
                                                            : (editingStaff ? 'Nuevo PIN Individual' : 'PIN Individual (obligatorio)')}
                                                    </span>
                                                    {editingStaff && !['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(staffForm.role) && (
                                                        <span className="bg-emerald-50 text-emerald-600 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider flex items-center gap-1">
                                                            <ShieldCheck className="w-3 h-3" /> Encriptado
                                                        </span>
                                                    )}
                                                </div>
                                            </label>

                                            {['SUPER_ADMIN', 'GROUP_ADMIN', 'HOSPITAL_ADMIN'].includes(staffForm.role) ? (
                                                <div className="animate-fade-in space-y-3">
                                                    <input
                                                        type="email"
                                                        value={staffForm.email || ''}
                                                        onChange={e => setStaffForm(f => ({ ...f, email: e.target.value.toLowerCase().trim() }))}
                                                        placeholder="ejemplo@hospital-status.com"
                                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-700 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                                    />
                                                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2">
                                                        <ShieldCheck className="w-5 h-5 text-amber-500 flex-shrink-0" />
                                                        <p className="text-xs text-amber-800 leading-relaxed font-medium">
                                                            <strong>Atención:</strong> Tendrás que registrar este correo temporalmente en el panel de <strong>Cloudflare Zero Trust (Teams)</strong> para que puedan acceder. Su portal de ingreso será <code className="bg-amber-100 px-1 rounded text-[10px]">{baseUrl}/hq</code>
                                                        </p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="animate-fade-in">
                                                    <div className="flex gap-4">
                                                        <div className="relative flex-1">
                                                            <input
                                                                type={showStaffFormPin ? 'text' : 'password'}
                                                                value={staffForm.pin || ''}
                                                                onChange={e => setStaffForm(f => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                                                placeholder="••••"
                                                                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3.5 text-slate-700 text-lg font-mono tracking-[0.5em] font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-shadow"
                                                                maxLength={4}
                                                            />
                                                            <button type="button" onClick={() => setShowStaffFormPin(!showStaffFormPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-indigo-600">
                                                                {showStaffFormPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                                            </button>
                                                        </div>
                                                        {(staffForm.pin && staffForm.pin.length === 4) && (
                                                            <div className="relative flex-1 animate-fade-in">
                                                                <input
                                                                    type={showStaffFormPin ? 'text' : 'password'}
                                                                    value={staffForm.pinConfirm || ''}
                                                                    onChange={e => setStaffForm(f => ({ ...f, pinConfirm: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                                                    placeholder="Confirma el PIN"
                                                                    className={`w-full bg-slate-50 border ${staffForm.pin && staffForm.pinConfirm && staffForm.pin !== staffForm.pinConfirm ? 'border-red-300 focus:ring-red-500' : 'border-slate-200 focus:ring-indigo-500'} rounded-xl px-4 py-3.5 text-slate-700 text-lg font-mono tracking-[0.5em] font-bold focus:outline-none focus:ring-2 transition-shadow`}
                                                                    maxLength={4}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                    {editingStaff && (
                                                        <p className="text-[10px] font-medium text-slate-400 mt-2 flex items-start gap-1">
                                                            <ShieldCheck className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                                                            Por seguridad, el PIN actual es secreto y no se puede revelar. Ingresa uno nuevo solo si necesitas restablecerlo; de lo contrario, déjalo vacío.
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {staffFormError && (
                                            <div className="flex items-center gap-2 bg-red-50 text-red-600 border border-red-100 rounded-xl px-4 py-3 text-sm font-bold">
                                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                                <p>{staffFormError}</p>
                                            </div>

                                        )}

                                        <div className="pt-4 flex gap-3">
                                            <button onClick={() => setIsStaffFormOpen(false)} className="px-6 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold text-sm transition-colors">Cancelar</button>
                                            <button
                                                onClick={handleStaffSave}
                                                disabled={isSavingStaff || (!!staffForm.pin && staffForm.pin.length < 4)}
                                                className="flex-1 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold text-sm shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                            >
                                                {isSavingStaff ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <SaveIcon />}
                                                {editingStaff ? 'Guardar Cambios' : 'Crear Usuario'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* TAB: AUDIT LOG */}
                    {activeTab === 'audit' && (
                        <div className="animate-fade-in flex flex-col h-full">
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h3 className="font-bold text-slate-800 text-lg">Registro de Auditoría</h3>
                                    <p className="text-sm text-slate-500">Historial completo de accesos y acciones del personal.</p>
                                </div>
                                <button
                                    onClick={() => loadAuditData(hospital.id)}
                                    className="p-2 border border-slate-200 text-slate-500 hover:bg-slate-100 rounded-lg transition-colors flex items-center gap-2 text-sm font-bold shadow-sm bg-white"
                                >
                                    <History className="w-4 h-4" /> Recargar
                                </button>
                            </div>

                            {isLoadingAudit ? (
                                <div className="flex-1 flex items-center justify-center p-12">
                                    <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
                                </div>
                            ) : auditLogs.length === 0 ? (
                                <div className="bg-white border border-slate-200 rounded-3xl p-12 text-center flex-1 flex flex-col items-center justify-center">
                                    <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-600 font-bold text-lg mb-2">No hay registros</p>
                                    <p className="text-slate-500 text-sm max-w-sm">
                                        El historial comenzará a llenarse cuando el personal empiece a interactuar con el sistema utilizando su número de PIN.
                                    </p>
                                </div>
                            ) : (
                                <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm relative">
                                    <div className="max-h-[50vh] overflow-y-auto">
                                        <table className="w-full relative">
                                            <thead className="bg-slate-50 border-b border-slate-200 text-left sticky top-0 z-10">
                                                <tr>
                                                    <th className="py-3 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Fecha / Hora</th>
                                                    <th className="py-3 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Usuario / Staff</th>
                                                    <th className="py-3 px-6 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Acción Realizada</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100">
                                                {auditLogs.map(log => (
                                                    <tr key={log.id} className="hover:bg-slate-50/50 transition-colors">
                                                        <td className="py-3 px-6 text-sm">
                                                            <div className="font-medium text-slate-700">{new Date(log.timestamp).toLocaleDateString()}</div>
                                                            <div className="text-[10px] font-mono text-slate-400 font-bold">{new Date(log.timestamp).toLocaleTimeString()}</div>
                                                        </td>
                                                        <td className="py-3 px-6">
                                                            <div className="flex items-center gap-2">
                                                                <div className={`w-2 h-2 rounded-full ${log.staffId === 'master' ? 'bg-amber-400' : 'bg-indigo-500'}`}></div>
                                                                <span className="text-sm font-bold text-slate-700">{log.staffName}</span>
                                                            </div>
                                                            {log.staffId === 'master' && <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider block w-fit mt-1">Uso de PIN Maestro</span>}
                                                        </td>
                                                        <td className="py-3 px-6">
                                                            <span className="text-sm text-slate-800 font-medium inline-block bg-slate-100 px-2 py-1 rounded-md border border-slate-200 shadow-sm">{log.action}</span>
                                                            {log.targetId && <div className="text-[10px] text-slate-400 font-mono mt-1 ml-1 break-all uppercase">ID: {log.targetId}</div>}
                                                            {log.detail && (
                                                                <div className="mt-1 text-xs text-slate-500 bg-slate-50 p-2 rounded -ml-2 border-l-2 border-indigo-300">
                                                                    {log.detail}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer contextual */}
                <div className="p-5 border-t border-slate-100 bg-slate-50 text-center flex justify-between items-center">
                    <div className="text-xs text-slate-500 font-medium flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4 text-emerald-500" /> Control de Acceso Activo
                    </div>
                    {!isStaffFormOpen && (
                        <button onClick={onClose} className="px-6 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-600 font-bold rounded-xl transition-colors shadow-sm text-sm">
                            Cerrar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

// Help-icon component extracted
const SaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path><polyline points="17 21 17 13 7 13 7 21"></polyline><polyline points="7 3 7 8 15 8"></polyline></svg>
);

export default AccessModal;
